import React, { useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import { getAccessToken } from '@functions/accessToken'
import {
  isAdmin as checkIfAdmin,
  isAgencyUser as checkIfAgencyUser,
  isBrandUser as checkIfBrandUser,
  isProviderUser as checkIfProviderUser,
} from '@functions/user'

import useCurrentUser from '@hooks/useCurrentUser'
import useGeoCountries from '@hooks/useGeoCountries'
import useLegacyRedirect from '@hooks/useLegacyRedirect'
import useMixpanel from '@hooks/useMixpanel'
import useRoles from '@hooks/useRoles'
import useStatuses from '@hooks/useStatuses'

import AdminRoutes from '@sections/Admin/routes'
import AccessDenied from '@sections/Auth/AccessDenied'
import ClientRoutes from '@sections/Client/routes'
import Login from '@sections/Auth/Login'
import PublicRoutes from '@sections/Public/routes'

// When logging in the token is not instantly available
// We wait and check 0.1 second later for it to be available

type SetAccessToken = (token: string | null) => React.Dispatch<React.SetStateAction<string | null>>

const waitForAccessToken = (setAccessToken: SetAccessToken) => {
  const intervalId = setInterval(() => {
    const token = getAccessToken()
    if (token){
      clearInterval(intervalId)
      setAccessToken(token)
    }
  }, 100)
}

const AuthenticationController = () => {
  const [accessToken, setAccessToken] = useState(null)

  const auth0Payload = useAuth0()
  const { isAuthenticated, user } = auth0Payload

  useEffect(() => {
    // @ts-expect-error TS2345
    waitForAccessToken(setAccessToken)
  }, [])

  const { 'https://www.realhubapp.com/role': authenticatedRoleTitle } = user || {}

  // Access token needs to be present to make requests
  const isAuthenticatedAdmin = accessToken && isAuthenticated && checkIfAdmin(authenticatedRoleTitle)
  const isAuthenticatedAgencyUser = accessToken && isAuthenticated && checkIfAgencyUser(authenticatedRoleTitle)
  const isAuthenticatedBrandUser = accessToken && isAuthenticated && checkIfBrandUser(authenticatedRoleTitle)
  const isAuthenticatedProviderUser = accessToken && isAuthenticated && checkIfProviderUser(authenticatedRoleTitle)

  const isAllowedRole = isAuthenticatedAdmin
    || isAuthenticatedAgencyUser
    || isAuthenticatedBrandUser
    || isAuthenticatedProviderUser

  const performHttpRequests = !!accessToken && isAuthenticated && isAllowedRole

  const currentUserPayload = useCurrentUser({ performHttpRequests })
  const {
    agency,
    callbacks: { logout },
    currentUser,
    isAdmin,
    isAgencyUser,
    isBrandUser,
    isProviderUser,
    canAccessInactiveAgency,
  } = currentUserPayload

  // We need to trigger a reload of the below entities when the redux store is cleared
  // when impersonating a user
  const currentUserId = currentUser?.id

  const { loading: loadingRoles } = useRoles({ deps: [currentUserId], performHttpRequests })
  useStatuses({ deps: [currentUserId], performHttpRequests })

  useGeoCountries({ deps: [currentUserId], performHttpRequests })

  useLegacyRedirect()

  const mixpanelPayload = useMixpanel()
  const {
    callbacks: { initMixpanel },
  } = mixpanelPayload

  useEffect(() => {
    if (currentUserId){
      // Initializing Mixpanel
      initMixpanel()
    }
  }, [currentUserId])

  const { publicToken } = parsePermittedQueryParams(getQueryParamsFromHash(), ['publicToken'])

  if (window.location.hash.startsWith('#/public/') && publicToken){
    return <PublicRoutes />
  }

  if (!accessToken || !isAuthenticated || !currentUserId || loadingRoles){
    return <Login />
  }

  if ((isAdmin || isBrandUser || isProviderUser)){
    return <AdminRoutes />
  }

  // Allow access to client routes if the user is an admin or brand user even
  if (canAccessInactiveAgency){
    return <ClientRoutes />
  }

  // Need to redirect to legacy Realhub if the agency is not active
  if (isAgencyUser && !agency.active){
    return (
      <AccessDenied accountLocked callbacks={{ logout: () => logout() }} />
    )
  }

  if (isAgencyUser){
    return <ClientRoutes />
  }

  return (
    <AccessDenied callbacks={{ logout: () => logout() }} />
  )
}

export default AuthenticationController
